/**
 * API Configuration
 * Central configuration for all API endpoints and settings
 */

import { XANO_API_GROUPS, XANO_HOST } from '../shared/constants';

// XANO_HOST is defined once in shared/constants (a zero-import leaf) and is
// imported directly from there. Deliberately NOT re-exported here: nothing
// consumes it via this module, and an unused re-export fails `fallow audit`.

// Base API URLs - will be replaced with environment variables
const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.aksanio-qbank.com/v1';

// Xano Backend Configuration - Build URL from base + API group + branch
const baseUrl = XANO_HOST;
const branch = import.meta.env.VITE_XANO_BRANCH || '';
const branchSuffix = branch ? `:${branch}` : '';
export const XANO_BASE_URL = `${baseUrl}/api:${XANO_API_GROUPS.AUTH}${branchSuffix}`;
export const XANO_API_KEY = XANO_API_GROUPS.AUTH;

// API Version
export const API_VERSION = 'v1';

// Request timeout (in milliseconds)
export const REQUEST_TIMEOUT = 30000; // 30 seconds

// Retry configuration
export const RETRY_CONFIG = {
  maxRetries: 3,
  retryDelay: 1000, // 1 second
  retryableStatuses: [408, 429, 500, 502, 503, 504],
};

/**
 * API Endpoints Configuration
 * Organized by resource with full endpoint definitions
 */
export const API_ENDPOINTS = {
  // Authentication endpoints
  auth: {
    login: {
      url: `${API_BASE_URL}/auth/login`,
      method: 'POST' as const,
      requiresAuth: false,
    },
    logout: {
      url: `${API_BASE_URL}/auth/logout`,
      method: 'POST' as const,
      requiresAuth: true,
    },
    refresh: {
      url: `${API_BASE_URL}/auth/refresh`,
      method: 'POST' as const,
      requiresAuth: false,
    },
    register: {
      url: `${API_BASE_URL}/auth/register`,
      method: 'POST' as const,
      requiresAuth: false,
    },
    forgotPassword: {
      url: `${API_BASE_URL}/auth/forgot-password`,
      method: 'POST' as const,
      requiresAuth: false,
    },
    resetPassword: {
      url: `${API_BASE_URL}/auth/reset-password`,
      method: 'POST' as const,
      requiresAuth: false,
    },
    verifyEmail: {
      url: `${API_BASE_URL}/auth/verify-email`,
      method: 'POST' as const,
      requiresAuth: false,
    },
  },

  // Question generation endpoints
  questions: {
    generate: {
      url: `${API_BASE_URL}/questions/generate`,
      method: 'POST' as const,
      requiresAuth: true,
      rateLimit: '100/hour',
    },
    generateBatch: {
      url: `${API_BASE_URL}/questions/generate-batch`,
      method: 'POST' as const,
      requiresAuth: true,
      rateLimit: '20/hour',
    },
    list: {
      url: `${API_BASE_URL}/questions`,
      method: 'GET' as const,
      requiresAuth: true,
    },
    get: {
      url: (id: string) => `${API_BASE_URL}/questions/${id}`,
      method: 'GET' as const,
      requiresAuth: true,
    },
    update: {
      url: (id: string) => `${API_BASE_URL}/questions/${id}`,
      method: 'PUT' as const,
      requiresAuth: true,
    },
    delete: {
      url: (id: string) => `${API_BASE_URL}/questions/${id}`,
      method: 'DELETE' as const,
      requiresAuth: true,
    },
    checkSimilarity: {
      url: `${API_BASE_URL}/questions/check-similarity`,
      method: 'POST' as const,
      requiresAuth: true,
    },
    export: {
      url: `${API_BASE_URL}/questions/export`,
      method: 'POST' as const,
      requiresAuth: true,
    },
  },

  // Review endpoints
  reviews: {
    queue: {
      url: `${API_BASE_URL}/reviews/queue`,
      method: 'GET' as const,
      requiresAuth: true,
    },
    submit: {
      url: `${API_BASE_URL}/reviews`,
      method: 'POST' as const,
      requiresAuth: true,
    },
    get: {
      url: (id: string) => `${API_BASE_URL}/reviews/${id}`,
      method: 'GET' as const,
      requiresAuth: true,
    },
    update: {
      url: (id: string) => `${API_BASE_URL}/reviews/${id}`,
      method: 'PUT' as const,
      requiresAuth: true,
    },
    assign: {
      url: `${API_BASE_URL}/reviews/assign`,
      method: 'POST' as const,
      requiresAuth: true,
      requiresRole: ['admin', 'team_lead'],
    },
    stats: {
      url: `${API_BASE_URL}/reviews/stats`,
      method: 'GET' as const,
      requiresAuth: true,
    },
  },

  // Budget endpoints
  budget: {
    current: {
      url: `${API_BASE_URL}/budget/current`,
      method: 'GET' as const,
      requiresAuth: true,
    },
    history: {
      url: `${API_BASE_URL}/budget/history`,
      method: 'GET' as const,
      requiresAuth: true,
      requiresRole: ['admin'],
    },
    update: {
      url: `${API_BASE_URL}/budget`,
      method: 'PUT' as const,
      requiresAuth: true,
      requiresRole: ['admin'],
    },
    allocate: {
      url: `${API_BASE_URL}/budget/allocate`,
      method: 'POST' as const,
      requiresAuth: true,
      requiresRole: ['admin'],
    },
    warnings: {
      url: `${API_BASE_URL}/budget/warnings`,
      method: 'GET' as const,
      requiresAuth: true,
    },
    override: {
      url: `${API_BASE_URL}/budget/override`,
      method: 'POST' as const,
      requiresAuth: true,
      requiresRole: ['admin'],
    },
  },

  // User management endpoints
  users: {
    list: {
      url: `${API_BASE_URL}/users`,
      method: 'GET' as const,
      requiresAuth: true,
      requiresRole: ['admin', 'team_lead'],
    },
    get: {
      url: (id: string) => `${API_BASE_URL}/users/${id}`,
      method: 'GET' as const,
      requiresAuth: true,
    },
    create: {
      url: `${API_BASE_URL}/users`,
      method: 'POST' as const,
      requiresAuth: true,
      requiresRole: ['admin'],
    },
    update: {
      url: (id: string) => `${API_BASE_URL}/users/${id}`,
      method: 'PUT' as const,
      requiresAuth: true,
      requiresRole: ['admin'],
    },
    delete: {
      url: (id: string) => `${API_BASE_URL}/users/${id}`,
      method: 'DELETE' as const,
      requiresAuth: true,
      requiresRole: ['admin'],
    },
    updateRole: {
      url: (id: string) => `${API_BASE_URL}/users/${id}/role`,
      method: 'PUT' as const,
      requiresAuth: true,
      requiresRole: ['admin'],
    },
    activity: {
      url: (id: string) => `${API_BASE_URL}/users/${id}/activity`,
      method: 'GET' as const,
      requiresAuth: true,
    },
    profile: {
      url: `${API_BASE_URL}/users/profile`,
      method: 'GET' as const,
      requiresAuth: true,
    },
    updateProfile: {
      url: `${API_BASE_URL}/users/profile`,
      method: 'PUT' as const,
      requiresAuth: true,
    },
  },

  // Analytics endpoints (future)
  analytics: {
    dashboard: {
      url: `${API_BASE_URL}/analytics/dashboard`,
      method: 'GET' as const,
      requiresAuth: true,
    },
    questions: {
      url: `${API_BASE_URL}/analytics/questions`,
      method: 'GET' as const,
      requiresAuth: true,
    },
    reviews: {
      url: `${API_BASE_URL}/analytics/reviews`,
      method: 'GET' as const,
      requiresAuth: true,
    },
    costs: {
      url: `${API_BASE_URL}/analytics/costs`,
      method: 'GET' as const,
      requiresAuth: true,
    },
  },

  // AI Model endpoints
  models: {
    list: {
      url: `${API_BASE_URL}/models`,
      method: 'GET' as const,
      requiresAuth: true,
    },
    pricing: {
      url: `${API_BASE_URL}/models/pricing`,
      method: 'GET' as const,
      requiresAuth: true,
    },
    availability: {
      url: `${API_BASE_URL}/models/availability`,
      method: 'GET' as const,
      requiresAuth: true,
    },
  },

  // Exam System endpoints
  exams: {
    types: {
      url: `${API_BASE_URL}/exam_types`,
      method: 'GET' as const,
      requiresAuth: true,
    },
    steps: {
      url: `${API_BASE_URL}/exam_steps`,
      method: 'GET' as const,
      requiresAuth: true,
    },
    pools: {
      url: `${API_BASE_URL}/question_pools`,
      method: 'GET' as const,
      requiresAuth: true,
    },
    createSession: {
      url: `${API_BASE_URL}/user_sessions/create`,
      method: 'POST' as const,
      requiresAuth: true,
    },
    activeSession: {
      url: `${API_BASE_URL}/user_sessions/active`,
      method: 'GET' as const,
      requiresAuth: true,
    },
    getSession: {
      url: (id: string) => `${API_BASE_URL}/user_sessions/${id}`,
      method: 'GET' as const,
      requiresAuth: true,
    },
    sessionQuestions: {
      url: (id: string) => `${API_BASE_URL}/user_sessions/${id}/questions`,
      method: 'GET' as const,
      requiresAuth: true,
    },
    submitAnswer: {
      url: `${API_BASE_URL}/user_question_attempts/submit`,
      method: 'POST' as const,
      requiresAuth: true,
    },
    completeSession: {
      url: `${API_BASE_URL}/user_sessions/complete`,
      method: 'POST' as const,
      requiresAuth: true,
    },
    abandonSession: {
      url: (id: string) => `${API_BASE_URL}/user_sessions/${id}/abandon`,
      method: 'POST' as const,
      requiresAuth: true,
    },
    sessionHistory: {
      url: `${API_BASE_URL}/user_sessions`,
      method: 'GET' as const,
      requiresAuth: true,
    },
    sessionResult: {
      url: (id: string) => `${API_BASE_URL}/user_sessions/${id}/result`,
      method: 'GET' as const,
      requiresAuth: true,
    },
  },
} as const;

/**
 * Helper function to get endpoint configuration
 */
export function getEndpoint(resource: keyof typeof API_ENDPOINTS, action: string) {
  const endpoints = API_ENDPOINTS[resource];
  return endpoints[action as keyof typeof endpoints];
}

/**
 * API Headers configuration
 */
export const getDefaultHeaders = (token?: string): HeadersInit => {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'X-API-Version': API_VERSION,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
};

/**
 * Error codes mapping
 */
export const API_ERROR_CODES = {
  // Authentication errors
  INVALID_CREDENTIALS: 'AUTH001',
  TOKEN_EXPIRED: 'AUTH002',
  INSUFFICIENT_PERMISSIONS: 'AUTH003',
  ACCOUNT_LOCKED: 'AUTH004',
  
  // Validation errors
  VALIDATION_FAILED: 'VAL001',
  INVALID_INPUT: 'VAL002',
  MISSING_REQUIRED_FIELD: 'VAL003',
  
  // Resource errors
  NOT_FOUND: 'RES001',
  ALREADY_EXISTS: 'RES002',
  CONFLICT: 'RES003',
  
  // Budget errors
  BUDGET_EXCEEDED: 'BUD001',
  BUDGET_WARNING: 'BUD002',
  
  // Rate limiting
  RATE_LIMIT_EXCEEDED: 'RATE001',
  
  // Server errors
  INTERNAL_ERROR: 'SRV001',
  SERVICE_UNAVAILABLE: 'SRV002',
} as const;